import Debug from 'debug';
import fetch from 'isomorphic-fetch';
import { UAParser } from 'ua-parser-js';

import { environment, getUrlsForEnvironment } from '../../environment';
import { getYardstickPath, getYardstickEnv } from '../../lib/metrics';
import { getAppVersion } from '../../lib/version';
import { IBaseMetric, UserDetails, IMetric, YardstickType } from '../../types/metrics';
import { Session } from '../lib/Session';
import { isNativeSend } from '../lib/host';
import { User } from '../models/User';
import { assignMetricModeContext } from '../lib/metricsUtil';
import { getHighestSupportedRequirementSet } from '../abstractions/office';
import { userService } from './UserService';

export enum LOG_TYPE {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
}

export interface ILog extends IBaseMetric {
  logType: LOG_TYPE;
  detail: unknown;
  message: unknown;
}

const session = new Session();
const ua = new UAParser();
function nop() {
  /* deliberate */
}

class Service {
  debug = Debug('o365:MetricService');
  info: { host: Office.HostType | 'unknown'; platform: Office.PlatformType | 'unknown' } = {
    host: 'unknown',
    platform: 'unknown',
  };

  constructor() {
    Office.onReady((info) => {
      this.info = info;
    });
  }

  serializeUser(user: User | null): UserDetails {
    const details: UserDetails = {
      clientId: 'unknown',
      email: 'unknown',
      subscriptionId: 'unknown',
      accountStatus: 'unknown',
    };
    if (user) {
      details.clientId = user.get('clientId');
      details.email = user.get('userName');
      details.subscriptionId = user.get('subscriptionId');
      details.accountStatus = user.get('subscriptionStatus');
    }
    return details;
  }

  getAppName() {
    const name = ['outlook365'];
    if (isNativeSend()) {
      name.push('native');
    }
    if (this.info.platform === Office.PlatformType.OfficeOnline) {
      name.push('web');
    } else {
      name.push('desktop');
    }

    return name.join('_');
  }

  getOSName() {
    const MACOS = 'macos';
    const name = ua.getOS().name ?? 'unknown';
    if (name === 'unknown' && this.info.platform !== 'unknown') {
      if (this.info.platform === Office.PlatformType.PC) {
        return 'windows';
      }
      if (this.info.platform === Office.PlatformType.Mac) {
        return MACOS;
      }
    }

    const nameMap: Record<string, string> = {
      mac: MACOS,
      'mac os': MACOS,
      mac_os: MACOS,
    };

    if (name !== 'unknown' && name in nameMap) {
      return nameMap[name];
    }

    return name.toLowerCase();
  }

  /**
   * Send a metric to metro/redshift
   *
   * @param metricName Name or names to send
   * @param videoId The video ID this event relates to, if any
   * @returns
   */
  increment = (metricName: string | string[], videoId: string | null = null): Promise<void> => {
    session.setActive();
    const names = Array.isArray(metricName) ? metricName : [metricName];
    return this.getBaseMetric()
      .then((baseMetric) => {
        const payload: IMetric = {
          ...baseMetric,
          names: names.map(assignMetricModeContext),
        };
        if (videoId) {
          payload.videoId = videoId;
        }
        this.debug('increment', names);
        const urls = getUrlsForEnvironment(environment);
        return fetch(`${urls.metroUrl}/metric`, {
          method: 'POST',
          body: JSON.stringify(payload, null, 0),
          headers: { 'Content-Type': 'application/json' },
        });
      })
      .then((response) => this.debug('sent metric with fetch', response), nop);
  };

  log = (information: unknown, logType: LOG_TYPE, detail?: unknown): Promise<void> => {
    return this.getBaseMetric()
      .then((baseMetric) => {
        const payload: ILog = {
          ...baseMetric,
          message: information,
          detail: {},
          logType,
        };

        if (typeof detail === 'object' && detail) {
          payload.detail = { ...detail };

          // If we're logging with a detail object that has a name property is generated from an
          // OfficeError object, and it matches error code 5001, we want to send a separate health
          // metric, since error 5001 pretty much destroys any chance we have of working with the
          // item object.
          const { name } = detail as { name: unknown };
          if (typeof name === 'string' && /^OfficeError.*\(5001\)$/.test(name)) {
            this.yardstick('error5001');
          }
        }

        if (logType === LOG_TYPE.ERROR) {
          console.error(information);
        } else if (logType === LOG_TYPE.WARNING) {
          console.warn(information);
        } else {
          console.info(information);
        }

        const urls = getUrlsForEnvironment(environment);
        return fetch(`${urls.metroUrl}/log`, {
          method: 'POST',
          body: JSON.stringify(payload, null, 0),
          headers: { 'Content-Type': 'application/json' },
        });
      })
      .then((response) => this.debug('sent metric with fetch', response), nop);
  };

  yardstick = (metric: string, value?: number, type: YardstickType = 'counter'): Promise<void> => {
    if (type === 'timing' && (value === null || value === undefined)) {
      throw new Error('Value must be specified if yardstick type is timing');
    }

    // eslint-disable-next-line no-param-reassign
    value ??= 1;

    const path = getYardstickPath(type);
    const urls = getUrlsForEnvironment(environment);
    const url = `${urls.yardstickUrl}/feature/o365/${metric}/${path}`;
    const data = {
      environment: getYardstickEnv(environment),
      xsrc: 'o365',
      value,
    };

    this.debug('yardstick', metric, value);
    return fetch(url, {
      body: JSON.stringify(data),
      cache: 'no-cache',
      credentials: 'omit',
      headers: { 'Content-Type': 'application/json' },
      keepalive: false,
      method: 'POST',
      mode: 'cors',
    }).then(nop, () => {
      this.log('Error sending health metric data', LOG_TYPE.ERROR, {
        metricUrl: url,
        metricPayload: data,
      });
    });
  };

  private getBaseMetric = async (): Promise<IBaseMetric> => ({
    app: {
      name: this.getAppName(),
      version: await getAppVersion(),
      environment,
      mailboxRequirementSet: getHighestSupportedRequirementSet(),
    },
    device: {
      os: {
        name: this.getOSName(),
        architecture: 'unknown',
        version: ua.getOS().version ?? 'unknown',
      },
      model: 'unknown_device',
      platform: {
        name: ua.getBrowser().name?.toLowerCase() ?? 'unknown',
        architecture: 'unknown',
        version: ua.getBrowser().version ?? 'unknown',
      },
    },
    sessionId: session.getId(),
    user: this.serializeUser(userService.getUser()),
  });
}

export const MetricService = new Service();
